import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { labReportsApi, type LabReportScope } from "@/api/labReports";
import { useChainReportHospitalScope } from "@/hooks/useChainReportHospitalScope";
import { ReportHospitalFilter } from "@/components/reports/ReportHospitalFilter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Download } from "lucide-react";
import { toast } from "sonner";

function isoStartOfDay(d: string) {
  return new Date(`${d}T00:00:00.000Z`).toISOString();
}
function isoEndOfDay(d: string) {
  return new Date(`${d}T23:59:59.999Z`).toISOString();
}

function fmt(n: number) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function LabPayoutReportPage() {
  const {
    showHospitalFilter,
    hospitalOptions,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    isSuperAdmin,
  } = useChainReportHospitalScope({ defaultScope: "consolidated" });

  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [scope, setScope] = useState<LabReportScope>("ALL");

  const fromIso = useMemo(() => isoStartOfDay(fromDate), [fromDate]);
  const toIso = useMemo(() => isoEndOfDay(toDate), [toDate]);

  const { data: payoutRes, isLoading } = useQuery({
    queryKey: ["lab-payout-report", fromIso, toIso, scope, reportHospitalId ?? "self"],
    queryFn: () => labReportsApi.labPayout({ from: fromIso, to: toIso, scope, hospitalId: reportHospitalId }),
    enabled: !!fromIso && !!toIso,
  });

  const report = payoutRes?.data?.data as
    | {
        byScope: Array<{
          scope: string;
          labRevenue: number;
          externalTestCost: number;
          netAfterExternal: number;
        }>;
        totals: {
          labRevenue: number;
          externalTestCost: number;
          netAfterExternal: number;
          otherLabCosts: number;
          netPoolForPayout: number;
          payoutAt35Pct: number;
          payoutAt40Pct: number;
          payoutAt45Pct: number;
        };
        otherCosts: Array<{ source: string; category: string; amount: number }>;
        byExternalVendor: Array<{
          vendorName: string;
          testCount: number;
          patientCount: number;
          labRevenue: number;
          externalTestCost: number;
          netAfterExternal: number;
        }>;
        highlightedVendorTotals: Array<{
          vendorName: string;
          testCount: number;
          patientCount: number;
          labRevenue: number;
          externalTestCost: number;
          netAfterExternal: number;
        }>;
        highlightedExternalCases: Array<{
          scope: string;
          patientName: string | null;
          uhid: string | null;
          vendorName: string;
          testName: string | null;
          labRevenue: number;
          externalTestCost: number;
        }>;
      }
    | undefined;

  const downloadPatientCsv = async () => {
    try {
      const res = await labReportsApi.labPayoutPatientWiseCsv({
        from: fromIso,
        to: toIso,
        scope,
        hospitalId: reportHospitalId,
      });
      const blob = res.data as unknown as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lab-payout-patient-wise-${fromDate}-to-${toDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to download CSV");
    }
  };

  const t = report?.totals;

  return (
    <div className="thms-page-shell space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Lab Payout</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Lab billing revenue minus external test vendor costs and recorded lab payout expenses
            (Expenses → Lab Payout / Outside Lab Payout). Net pool shows suggested shares at 35%, 40%,
            and 45%.
          </p>
        </div>
        <Button variant="outline" onClick={downloadPatientCsv} className="gap-2">
          <Download className="h-4 w-4" /> Patient-wise CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm font-medium mb-1">From</div>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">To</div>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Scope</div>
            <Select value={scope} onValueChange={(v: LabReportScope) => setScope(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">OPD + IPD + Daycare</SelectItem>
                <SelectItem value="OPD">OPD only</SelectItem>
                <SelectItem value="IPD">IPD only</SelectItem>
                <SelectItem value="DAYCARE">Daycare only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {showHospitalFilter ? (
            <ReportHospitalFilter
              hospitals={hospitalOptions}
              value={selectedHospitalId}
              onChange={setSelectedHospitalId}
              isSuperAdmin={isSuperAdmin}
            />
          ) : (
            <div />
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : !report ? (
        <div className="text-sm text-muted-foreground">No data</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Lab revenue</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{fmt(t?.labRevenue ?? 0)}</CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">External test costs</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold text-amber-800">
                − {fmt(t?.externalTestCost ?? 0)}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Other lab costs</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold text-amber-800">
                − {fmt(t?.otherLabCosts ?? 0)}
              </CardContent>
            </Card>
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Net pool for payout</CardTitle>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{fmt(t?.netPoolForPayout ?? 0)}</CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Payout buckets (of net pool)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border p-4">
                <div className="text-sm text-muted-foreground">35%</div>
                <div className="text-xl font-semibold">{fmt(t?.payoutAt35Pct ?? 0)}</div>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-sm text-muted-foreground">40%</div>
                <div className="text-xl font-semibold">{fmt(t?.payoutAt40Pct ?? 0)}</div>
              </div>
              <div className="rounded-lg border p-4">
                <div className="text-sm text-muted-foreground">45%</div>
                <div className="text-xl font-semibold">{fmt(t?.payoutAt45Pct ?? 0)}</div>
              </div>
            </CardContent>
          </Card>

          {(report.highlightedVendorTotals?.length ?? 0) > 0 ? (
            <Card className="border-amber-200 bg-amber-50/40">
              <CardHeader>
                <CardTitle>Health Quest &amp; Orbit (external sends)</CardTitle>
                <p className="text-sm text-muted-foreground font-normal">
                  Patient lab revenue billed in THMS for tests marked external and sent to vendors whose
                  name matches Health Quest or Orbit.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {report.highlightedVendorTotals.map((v) => (
                    <div key={v.vendorName} className="rounded-lg border bg-white p-4">
                      <div className="font-semibold text-amber-950">{v.vendorName}</div>
                      <div className="mt-2 text-sm text-muted-foreground">
                        {v.testCount} test{v.testCount === 1 ? "" : "s"} · {v.patientCount} patient
                        {v.patientCount === 1 ? "" : "s"}
                      </div>
                      <div className="mt-2 text-lg font-semibold tabular-nums">{fmt(v.labRevenue)}</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Vendor cost −{fmt(v.externalTestCost)} · Net {fmt(v.netAfterExternal)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="overflow-auto rounded-md border bg-white">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="text-left py-2 px-3">Vendor</th>
                        <th className="text-left py-2 px-3">Scope</th>
                        <th className="text-left py-2 px-3">Patient</th>
                        <th className="text-left py-2 px-3">UHID</th>
                        <th className="text-left py-2 px-3">Test</th>
                        <th className="text-right py-2 px-3">Lab revenue</th>
                        <th className="text-right py-2 px-3">Vendor cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.highlightedExternalCases.map((row, i) => (
                        <tr key={`${row.vendorName}-${row.uhid}-${row.testName}-${i}`} className="border-b">
                          <td className="py-2 px-3 font-medium">{row.vendorName}</td>
                          <td className="py-2 px-3">{row.scope}</td>
                          <td className="py-2 px-3">{row.patientName ?? "—"}</td>
                          <td className="py-2 px-3 tabular-nums">{row.uhid ?? "—"}</td>
                          <td className="py-2 px-3">{row.testName ?? "—"}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmt(row.labRevenue)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmt(row.externalTestCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {(report.byExternalVendor?.length ?? 0) > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>All external lab vendors</CardTitle>
              </CardHeader>
              <CardContent className="overflow-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">Vendor</th>
                      <th className="text-right py-2 pr-4">Tests</th>
                      <th className="text-right py-2 pr-4">Patients</th>
                      <th className="text-right py-2 pr-4">Lab revenue</th>
                      <th className="text-right py-2 pr-4">Vendor cost</th>
                      <th className="text-right py-2 pr-4">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byExternalVendor.map((v) => (
                      <tr
                        key={v.vendorName}
                        className={
                          /health\s*quest|orbit/i.test(v.vendorName)
                            ? "border-b bg-amber-50/60"
                            : "border-b"
                        }
                      >
                        <td className="py-2 pr-4 font-medium">{v.vendorName}</td>
                        <td className="py-2 pr-4 text-right">{v.testCount}</td>
                        <td className="py-2 pr-4 text-right">{v.patientCount}</td>
                        <td className="py-2 pr-4 text-right">{fmt(v.labRevenue)}</td>
                        <td className="py-2 pr-4 text-right">{fmt(v.externalTestCost)}</td>
                        <td className="py-2 pr-4 text-right">{fmt(v.netAfterExternal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Revenue &amp; external cost by scope</CardTitle>
            </CardHeader>
            <CardContent className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-4">Scope</th>
                    <th className="text-right py-2 pr-4">Lab revenue</th>
                    <th className="text-right py-2 pr-4">External test cost</th>
                    <th className="text-right py-2 pr-4">Net after external</th>
                  </tr>
                </thead>
                <tbody>
                  {report.byScope.map((r) => (
                    <tr key={r.scope} className="border-b">
                      <td className="py-2 pr-4">{r.scope}</td>
                      <td className="py-2 pr-4 text-right">{fmt(r.labRevenue)}</td>
                      <td className="py-2 pr-4 text-right">{fmt(r.externalTestCost)}</td>
                      <td className="py-2 pr-4 text-right">{fmt(r.netAfterExternal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Other lab costs (expenses)</CardTitle>
            </CardHeader>
            <CardContent>
              {!report.otherCosts?.length ? (
                <p className="text-sm text-muted-foreground">No lab payout expenses in this period.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">Source</th>
                      <th className="text-left py-2 pr-4">Category</th>
                      <th className="text-right py-2 pr-4">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.otherCosts.map((r, i) => (
                      <tr key={`${r.source}-${r.category}-${i}`} className="border-b">
                        <td className="py-2 pr-4">{r.source === "CENTRAL" ? "Central expense" : "IPD patient expense"}</td>
                        <td className="py-2 pr-4">{r.category.replace(/_/g, " ")}</td>
                        <td className="py-2 pr-4 text-right">{fmt(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
